import dayjs from 'dayjs';
import Controlbar from '../controls/Controlbar';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { fetchFile } from '../Utilities';
import { isMobile } from 'react-device-detect';

const fsNavButtonSx = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 40, height: 40, borderRadius: '50%',
    bgcolor: 'rgba(43, 51, 63, 0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', zIndex: 2,
    '&:hover': { bgcolor: 'rgba(43, 51, 63, 0.9)' }
}

export default function AccessImageCard({ elem, imgSrc, isLoaded, vehicleLabel, onLoaded, onFailed, t, allEvents, currentIndex, baseUrl, fallbackUrl, getVehicleLabel: getLabel, direction }) {
    
    const [zoom, setZoom] = useState(1)
    const [fsIndex, setFsIndex] = useState(currentIndex)

    // in fullscreen: show navigated event; otherwise show own
    const fullscreenHandle = useFullScreenHandle()
    const fsEvent = fullscreenHandle.active ? allEvents[fsIndex] : null
    const displaySrc = fsEvent?.bestImageName ? `${baseUrl}${fsEvent.bestImageName}` : (fsEvent ? fallbackUrl : imgSrc)
    const displayEvent = fsEvent ?? elem
    const displayLabel = fsEvent ? getLabel(fsEvent) : vehicleLabel
    const displayTimestamp = dayjs(displayEvent.timestamp).format(t("date.format.full_date_time"))
    const displayFilename = `access_${dayjs(displayEvent.timestamp).format('YYYY-MM-DD_HH-mm')}.jpg`
    const hasPrev = fullscreenHandle.active && fsIndex > 0
    const hasNext = fullscreenHandle.active && fsIndex < allEvents.length - 1
    const zoomResetRef = useRef(null)

    useEffect(() => {
        if (!fullscreenHandle.active) return
        const total = allEvents.length
        const handler = (event) => {
            if (event.key === 'Escape') {
                fullscreenHandle.exit()
            } else if (event.key === 'ArrowLeft') {
                zoomResetRef.current?.()
                setFsIndex(prev => Math.max(0, prev - 1))
            } else if (event.key === 'ArrowRight') {
                zoomResetRef.current?.()
                setFsIndex(prev => Math.min(total - 1, prev + 1))
            }
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [fullscreenHandle.active, allEvents.length]) // eslint-disable-line react-hooks/exhaustive-deps

    function PanningInteractionHandler(instance, state) {
        if (state.scale > 1) {
            instance.setup.panning.disabled = false
        } else {
            instance.setup.panning.disabled = true
        }
    }

    return (
        <Box sx={{
            height: isLoaded ? "auto" : 0,
            overflow: "hidden",
            mb: isLoaded ? 0.5 : 0,
            backgroundColor: isLoaded ? 'white' : 'transparent',
            borderBottomLeftRadius: isLoaded ? 6 : 0,
            borderBottomRightRadius: isLoaded ? 6 : 0
        }}>
            <TransformWrapper
                disabled={!isLoaded}
                disablePadding
                doubleClick={{ disabled: true }}
                wheel={{ disabled: !fullscreenHandle.active }}
                onZoomStop={ref => PanningInteractionHandler(ref.instance, ref.state)}
                onPanning={ref => PanningInteractionHandler(ref.instance, ref.state)}
                onInit={ref => ref.instance.setup.panning.disabled = true}
                onTransformed={ref => setZoom(ref.state.scale)}
            >
                {(utils) => {
                    zoomResetRef.current = utils.resetTransform
                    return (
                        <FullScreen
                            handle={fullscreenHandle}
                            onChange={(active) => {
                                utils.resetTransform()
                                if (active) setFsIndex(currentIndex)
                            }}
                        >
                            <Box className={isMobile ? "" : "fullscreen"} sx={{ position: 'relative' }}>
                                <TransformComponent
                                    wrapperStyle={{ width: '100%', height: '100%' }}
                                    contentStyle={{ width: '100%', height: '100%' }}
                                    wrapperProps={{ onDoubleClick: () => fullscreenHandle.active ? fullscreenHandle.exit() : fullscreenHandle.enter() }}
                                >
                                    <img
                                        src={displaySrc}
                                        alt={displayEvent.bestImageName || "image"}
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                            objectFit: fullscreenHandle.active ? "contain" : "cover",
                                            height: fullscreenHandle.active ? "100%" : "auto",
                                            backgroundColor: fullscreenHandle.active ? "black" : "transparent"
                                        }}
                                        loading="lazy"
                                        ref={img => {
                                            if (img && img.complete && img.naturalWidth > 0) onLoaded(elem.id)
                                        }}
                                        onLoad={() => onLoaded(elem.id)}
                                        onError={() => onFailed(elem.id)}
                                    />
                                </TransformComponent>
                                {hasPrev && (
                                    <Box className={isMobile ? "" : "nav-arrow"} onClick={() => { utils.resetTransform(); setFsIndex(prev => prev - 1) }} onDoubleClick={e => e.stopPropagation()}
                                        sx={{ ...fsNavButtonSx, left: 8, ...(!isMobile && { opacity: 0, transition: '0.5s ease' }) }}>
                                        <ArrowBackIos sx={{ color: 'white', fontSize: 18, ml: '4px' }} />
                                    </Box>
                                )}
                                {hasNext && (
                                    <Box className={isMobile ? "" : "nav-arrow"} onClick={() => { utils.resetTransform(); setFsIndex(prev => prev + 1) }} onDoubleClick={e => e.stopPropagation()}
                                        sx={{ ...fsNavButtonSx, right: 8, ...(!isMobile && { opacity: 0, transition: '0.5s ease' }) }}>
                                        <ArrowForwardIos sx={{ color: 'white', fontSize: 18 }} />
                                    </Box>
                                )}
                                {isLoaded && (
                                    <Controlbar
                                        fullscreenHandle={fullscreenHandle}
                                        zoom={zoom}
                                        zoomUtils={utils}
                                        handleTakeSnapshot={() => fetchFile(displaySrc, displayFilename)}
                                        imageSource={displaySrc}
                                        hasLastModified={false}
                                        hasZoom={isMobile || fullscreenHandle.active}
                                    />
                                )}
                            </Box>
                            {fullscreenHandle.active && (
                                <Box className="Title" sx={{ zIndex: 2 }}>
                                    <Box className="Title-Label">
                                        <Typography variant="body1" className="Title-CameraLabel">
                                            {direction === "entry" ? t("pages.access.entry") : t("pages.access.exit")}
                                            {displayLabel ? ` | ${displayLabel}` : ""}
                                            {` | ${displayTimestamp}`}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </FullScreen>
                    )
                }}
            </TransformWrapper>
            {isLoaded && (
                <Typography variant="body1" align="center" sx={{ mt: 0.5, userSelect: "none" }}>
                    {vehicleLabel ? `${vehicleLabel} | ` : ""}
                    {dayjs(elem.timestamp).format(t("date.format.full_date_time"))}
                </Typography>
            )}
        </Box>
    )
}